import { Platform } from "react-native";
import type { GraphQLResponse as RelayGraphQLResponse } from "relay-runtime";

type GraphQLRequestHeaders = Record<string, string>;

export type GraphQLRequestOptions = {
  method: "POST";
  credentials: RequestCredentials;
  headers: GraphQLRequestHeaders;
  body: string;
};

type GraphQLResponseHeaders = {
  get: (name: string) => string | null;
};

export type GraphQLHttpResponse = {
  ok: boolean;
  status: number;
  statusText: string;
  headers: GraphQLResponseHeaders;
  json: () => Promise<RelayGraphQLResponse>;
};

type XMLHttpRequestCallback = XMLHttpRequest["onload"] | (() => void);

type XMLHttpRequestLike = {
  status: number;
  statusText: string;
  responseText: string;
  withCredentials: boolean;
  onload: XMLHttpRequestCallback | null;
  onerror: XMLHttpRequestCallback | null;
  ontimeout: XMLHttpRequestCallback | null;
  onabort: XMLHttpRequestCallback | null;
  open: (method: string, url: string, async: boolean) => void;
  setRequestHeader: (name: string, value: string) => void;
  getAllResponseHeaders: () => string;
  send: (body: string) => void;
};

type XMLHttpRequestConstructor = new () => XMLHttpRequestLike;

export const requestGraphQL = async (
  url: string,
  options: GraphQLRequestOptions
): Promise<GraphQLHttpResponse> => {
  if (Platform.OS === "web") {
    return fetch(url, options);
  }

  return requestGraphQLWithXMLHttpRequest(url, options);
};

export const requestGraphQLWithXMLHttpRequest = (
  url: string,
  options: GraphQLRequestOptions,
  XMLHttpRequestImplementation: XMLHttpRequestConstructor = XMLHttpRequest
): Promise<GraphQLHttpResponse> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequestImplementation();

    xhr.onload = () => {
      const responseText = xhr.responseText;

      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        headers: createGraphQLResponseHeaders(xhr.getAllResponseHeaders()),
        json: () => Promise.resolve(JSON.parse(responseText)),
      });
    };
    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
    xhr.onabort = () => reject(new TypeError("Network request aborted"));

    xhr.open(options.method, url, true);
    xhr.withCredentials = options.credentials === "include";

    Object.entries(options.headers).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.send(options.body);
  });
};

const createGraphQLResponseHeaders = (
  rawHeaders: string
): GraphQLResponseHeaders => {
  const headers = new Map<string, string>();

  rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .filter((line) => line.length > 0)
    .forEach((line) => {
      const separatorIndex = line.indexOf(":");

      if (separatorIndex === -1) {
        return;
      }

      const name = line.slice(0, separatorIndex).trim().toLowerCase();
      const value = line.slice(separatorIndex + 1).trim();
      const existingValue = headers.get(name);

      headers.set(name, existingValue ? `${existingValue}, ${value}` : value);
    });

  return {
    get: (name: string) => headers.get(name.toLowerCase()) ?? null,
  };
};
